import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';

const FormContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1e4d92 0%, #2d8fe5 100%);
  padding: 2rem;
`;

const FormCard = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  padding: 3rem;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #1e4d92;
  text-align: center;
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    font-size: 1.2rem;
    color: #333;
    margin-bottom: 0.5rem;
  }

  input, select, textarea {
    width: 100%;
    padding: 1rem;
    font-size: 1.1rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    transition: all 0.3s ease;

    &:focus {
      border-color: #1e4d92;
      box-shadow: 0 0 0 2px rgba(30, 77, 146, 0.1);
      outline: none;
    }
  }

  textarea {
    min-height: 100px;
    resize: vertical;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1.2rem;
  font-size: 1.3rem;
  font-weight: bold;
  color: white;
  background: #1e4d92;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 2rem;

  &:hover {
    background: #163a6f;
    transform: translateY(-2px);
  }
`;

const ImageUploadContainer = styled.div`
  margin-bottom: 2rem;
  text-align: center;
`;

const ImagePreview = styled.img`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  object-fit: cover;
  margin: 1rem 0;
  border: 3px solid #1e4d92;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
`;

const UploadButton = styled.label`
  display: inline-block;
  padding: 0.8rem 1.5rem;
  background: #1e4d92;
  color: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #163a6f;
  }

  input {
    display: none;
  }
`;

const RegistrationForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    bloodType: '',
    emergencyContact: '',
    emergencyPhone: '',
    medicalConditions: '',
    photo: null
  });
  const [photoPreview, setPhotoPreview] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        photo: file
      });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Iniciando submissão do formulário...');
      
      // Remover o objeto File do formData
      const { photo, ...formDataWithoutFile } = formData;
      
      // Preparar dados para salvar
      const userData = {
        ...formDataWithoutFile,
        photo: photoPreview ? photoPreview : null,
        createdAt: new Date().toISOString()
      };
      
      console.log('Tentando conectar ao Firestore...');
      const usersRef = collection(db, 'users');
      console.log('Coleção referenciada com sucesso');
  
      // Salvar no Firestore
      const docRef = await addDoc(usersRef, userData);
      console.log('Documento salvo com ID:', docRef.id);
  
      // Navegar para a página de confirmação
      navigate('/confirm', { 
        state: { 
          formData: { ...formDataWithoutFile, id: docRef.id }, 
          photoPreview 
        } 
      });
    } catch (error) {
      console.error('Erro detalhado:', error);
      alert(`Erro ao salvar os dados: ${error.message}`);
    }
  };

  return (
    <FormContainer>
      <FormCard>
        <Title>Cadastro Médico</Title>
        <form onSubmit={handleSubmit}>
          <ImageUploadContainer>
            <ImagePreview 
              src={photoPreview || 'https://via.placeholder.com/150'} 
              alt="Prévia da foto" 
            />
            <br />
            <UploadButton>
              Adicionar Foto
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
              />
            </UploadButton>
          </ImageUploadContainer>
          <FormGroup>
            <label>Nome Completo</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </FormGroup>
  
          <FormGroup>
            <label>E-mail</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </FormGroup>
  
          <FormGroup>
            <label>Telefone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
            />
          </FormGroup>
  
          <FormGroup>
            <label>Tipo Sanguíneo</label>
            <select 
              name="bloodType" 
              value={formData.bloodType} 
              onChange={handleChange}
              required
            >
              <option value="">Selecione</option>
              <option value="A+">A+</option>
              <option value="A-">A-</option>
              <option value="B+">B+</option>
              <option value="B-">B-</option>
              <option value="AB+">AB+</option>
              <option value="AB-">AB-</option>
              <option value="O+">O+</option>
              <option value="O-">O-</option>
            </select>
          </FormGroup>
  
          <FormGroup>
            <label>Contato de Emergência</label>
            <input
              type="text"
              name="emergencyContact"
              value={formData.emergencyContact}
              onChange={handleChange}
              required
            />
          </FormGroup>
  
          <FormGroup>
            <label>Telefone de Emergência</label>
            <input
              type="tel"
              name="emergencyPhone"
              value={formData.emergencyPhone}
              onChange={handleChange}
              required
            />
          </FormGroup>
  
          <FormGroup>
            <label>Condições Médicas</label>
            <textarea
              name="medicalConditions"
              value={formData.medicalConditions}
              onChange={handleChange}
              placeholder="Liste suas condições médicas, alergias, medicamentos..."
            />
          </FormGroup>
  
          <SubmitButton type="submit">
            Cadastrar
          </SubmitButton>
        </form>
      </FormCard>
    </FormContainer>
  );
};

export default RegistrationForm;

